import React, { useCallback, useMemo, useState } from 'react';
import CardWithDivider from '../../../../components/Layout/CardWithDivider';
import TopAreaContainer from '../../../binders/components/TopAreaContainer';
import CardTitle from '../../../../components/typography/CardTitle';
import { NavLink } from 'react-router-dom-v5-compat';
import { L } from '../../../../lib/i18n';
import styled from '@emotion/styled';
import {
    createColumnHelper,
    getCoreRowModel,
    useReactTable,
    SortingState,
    getSortedRowModel,
    Row as RowType,
} from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom-v5-compat';
import EmptyState from '../../../../components/EmptyStateCommunity/EmptyStateCommunity';
import { Button } from '@ourliving/ourliving-ui';
import GenericTableVirtualized from '../../../binders/components/GenericTableVirtualizedInfinite';
import FilterSearchContainer from '../../../binders/components/emotion/Filters/FilterSearchContainer';
import GlobalFilter from '../../../../components/Table/FilterComponents/GlobalFilterTable';
import Row from '../../../binders/components/emotion/Row';
import { useGetInfiniteProjectTemplates } from '../../hooks/ProjectTemplates/projectTemplateQueries';

export type FiltersSpaces = {
    space_type: { value: string; label: string }[];
};

const CellContainer = styled.div({
    display: 'grid',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    maxWidth: '100%',
    fontSize: '0.75rem',
    gap: '0.2rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
});

const Flex = styled.div({
    display: 'flex',
});

const Data = styled.p({
    margin: '0px',
    textAlign: 'left',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
});

type ProjectTemplate = NonNullable<ReturnType<typeof useGetInfiniteProjectTemplates>['data']>['pages'][0]['items'][0];

const columnHelper = createColumnHelper<ProjectTemplate>();

const TemplatesPage = () => {
    const columns = useMemo(
        () => [
            columnHelper.accessor((row) => row.name, {
                id: 'name',
                cell: (info) => (
                    <CellContainer>
                        <Data>{info.getValue()}</Data>
                    </CellContainer>
                ),
                header: () => <Flex>{L('template_name')}</Flex>,
                minSize: 110,
                size: 150,
                meta: {
                    styles: {
                        flex: '1 0 auto',
                    },
                },
            }),
            columnHelper.accessor((row) => row.type_name, {
                id: 'type_name',
                cell: (info) => (
                    <CellContainer>
                        <Data>{L(info.row.original.type_name)}</Data>
                    </CellContainer>
                ),
                header: () => <Flex>{L('project_type_literally')}</Flex>,
                minSize: 110,
                size: 150,
                meta: {
                    styles: {
                        flex: '1 0 auto',
                    },
                },
            }),
        ],
        [],
    );

    const navigate = useNavigate();

    const searchParams = new URLSearchParams(location.search);

    const searchField = useMemo(() => searchParams.get('searchField') ?? '', [searchParams]);

    const handleSearch = (value: string) => {
        searchParams.set('searchField', value);
        navigate({
            search: searchParams.toString(),
        });
    };

    const [sorting, setSorting] = useState<SortingState>([]);

    const { fetchNextPage, hasNextPage, isFetching, data } = useGetInfiniteProjectTemplates({
        sorting,
        search: searchField,
    });

    const flatData = useMemo(() => data?.pages?.flatMap((page) => page.items) ?? [], [data]);
    const totalDBRowCount = useMemo(() => Number(data?.pages?.[0]?.totalItems ?? 0), [data]);
    const totalFetched = useMemo(() => flatData.length, [flatData]);

    const table = useReactTable({
        data: flatData,
        columns,
        manualSorting: true,
        state: {
            sorting,
        },
        onSortingChange: setSorting,
        sortDescFirst: false,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        columnResizeMode: 'onChange',
        enableColumnResizing: true,
    });

    const handleClickRow = useCallback((row: RowType<ProjectTemplate>) => {
        navigate(`${row.original.id}`);
    }, []);

    return (
        <CardWithDivider
            topArea={
                <TopAreaContainer>
                    <CardTitle>
                        {L('project_templates')} {data ? `(${data?.pages[0].totalItems})` : ''}
                    </CardTitle>
                    <NavLink to={'new'} style={{ marginLeft: 'auto' }}>
                        <Button>{L('new_project_template')}</Button>
                    </NavLink>
                </TopAreaContainer>
            }
            mainArea={
                <>
                    <FilterSearchContainer>
                        <div style={{ marginLeft: 'auto' }}>
                            <Row>
                                <GlobalFilter
                                    filter={searchField ?? ''}
                                    setFilter={handleSearch}
                                    placeholder={L('search')}
                                />
                            </Row>
                        </div>
                    </FilterSearchContainer>

                    <GenericTableVirtualized
                        table={table}
                        handleFetchNextPage={() => fetchNextPage()}
                        hasNextPage={hasNextPage}
                        isFetching={isFetching}
                        totalDBRowCount={totalDBRowCount}
                        totalFetched={totalFetched}
                        estimateSize={50}
                        rowClickHandler={handleClickRow}
                    />
                    {flatData?.length === 0 &&
                        data &&
                        (!searchField ? (
                            <EmptyState>
                                <EmptyState.Title>{L('no_project_templates')}</EmptyState.Title>
                                <EmptyState.Description>
                                    <p>{L('no_project_templates_description')}</p>
                                    <p>{L('click_button_below')}</p>
                                    <Button style={{ marginLeft: 'auto' }} onClick={() => navigate('new')}>
                                        {L('new_project_template')}
                                    </Button>
                                </EmptyState.Description>
                            </EmptyState>
                        ) : (
                            <EmptyState>
                                <EmptyState.Title>{L('no_project_templates_found')}</EmptyState.Title>
                                <EmptyState.Description>
                                    <p>{L('no_project_templates_found_description')}</p>
                                    <p>{L('please_try_something_else')}</p>
                                </EmptyState.Description>
                            </EmptyState>
                        ))}
                </>
            }
        />
    );
};

export default TemplatesPage;
